import { ErrorPanel, LinearProgress } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus } from "@octopusdeploy/octopus-react-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type { ReactNode } from "react";
import React, { useCallback, useMemo } from "react";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { useActiveWorkflowLoadingState } from "~/workflows/ActiveWorkflowLoadingState";
import { useDrawerOutPortalProvider } from "~/workflows/useDrawerNode";
import type { DialogWorkflow, DrawerWorkflow } from "~/workflows/workflows";
export const WorkflowContent = {
    Dialog: DialogWorkflowContent,
    Drawer: DrawerWorkflowContent,
};
interface DialogWorkflowContentProps {
    workflow: DialogWorkflow<unknown, unknown>;
    type: "save"; // todo: Redesign component so that this prop is not needed
    title: string;
    onSaveClick: () => Promise<void>;
    children: ReactNode;
}
function DialogWorkflowContent({ type, title, onSaveClick, children }: DialogWorkflowContentProps) {
    const { isLoading, loadingError } = useActiveWorkflowLoadingState();
    const { errors, isInProgress } = useAggregateAPIOperationStatus();
    const combinedErrors = useMemo(() => {
        if (loadingError !== null) {
            return [loadingError, ...errors];
        }
        return errors;
    }, [errors, loadingError]);
    const saveClick = useCallback(async () => {
        try {
            await onSaveClick();
            return true;
        }
        catch (error) {
            return false;
        }
    }, [onSaveClick]);
    if (type === "save") {
        return (<SaveDialogLayout title={title} onSaveClick={saveClick} busy={isLoading || isInProgress} errors={combinedErrors}>
                {children}
            </SaveDialogLayout>);
    }
    exhaustiveCheck(type, "Unknown dialog content type");
}
interface DrawerWorkflowContentProps {
    workflow: DrawerWorkflow<unknown, unknown>;
    title: string;
    isPrimaryButtonDisabled?: boolean;
    onPrimaryButtonClick: () => void;
    children: ReactNode;
}
function DrawerWorkflowContent({ title, children, onPrimaryButtonClick, isPrimaryButtonDisabled }: DrawerWorkflowContentProps) {
    const DrawerOutPortal = useDrawerOutPortalProvider();
    const { errors, isInProgress } = useAggregateAPIOperationStatus();
    return (<DrawerOutPortal title={title} onPrimaryButtonClick={onPrimaryButtonClick} isPrimaryButtonDisabled={isPrimaryButtonDisabled}>
            <LinearProgress variant={"indeterminate"} show={isInProgress}/>
            {errors.map(createErrorInfoFromError).map((error, index) => (<ErrorPanel key={index} error={error}/>))}
            {children}
        </DrawerOutPortal>);
}
